import { useState, useEffect, useRef } from 'react';

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [display, setDisplay] = useState(value);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          animateValue(value, duration);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  const animateValue = (target, dur) => {
    // Extract numeric part
    const match = target.match(/^([\d,.]+)(.*)$/);
    if (!match) {
      setDisplay(target);
      return;
    }

    const numStr = match[1].replace(/,/g, '');
    const suffix = match[2] || '';
    const end = parseFloat(numStr);
    const isFloat = numStr.includes('.');
    const hasComma = match[1].includes(',');

    if (isNaN(end)) {
      setDisplay(target);
      return;
    }

    const startTime = performance.now();

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / dur, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;

      if (isFloat) {
        const decimals = (numStr.split('.')[1] || '').length;
        setDisplay(current.toFixed(decimals) + suffix);
      } else {
        let formatted = Math.floor(current).toString();
        if (hasComma) {
          formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        setDisplay(formatted + suffix);
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setDisplay(target);
      }
    };

    requestAnimationFrame(step);
  };

  return <span ref={ref}>{display}</span>;
};

export default AnimatedCounter;
